import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Trash2, ChevronRight, Pencil, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { fetchClients, deleteClient, updateClient } from '../../lib/bookingsApi';
import ConfirmDialog from '../../components/ui/ConfirmDialog';

// ── Edit modal ────────────────────────────────────────────────────────────────
function ClientEditModal({ client, onClose, onSaved }) {
  const [form, setForm] = useState({
    name:          client.name          || '',
    email:         client.email         || '',
    phone:         client.phone         || '',
    date_of_birth: client.date_of_birth || client.dob || '',
    notes:         client.notes         || '',
  });
  const [saving, setSaving]   = useState(false);
  const [error,  setError]    = useState(null);

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name is required'); return; }
    setSaving(true);
    setError(null);
    try {
      const updated = await updateClient(client.id, {
        name:          form.name.trim(),
        email:         form.email  || null,
        phone:         form.phone  || null,
        date_of_birth: form.date_of_birth || null,
        notes:         form.notes  || null,
      });
      onSaved(updated);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.4)' }}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-surface-border">
          <h2 className="text-sm font-semibold text-ink">Edit Client</h2>
          <button onClick={onClose} className="p-1 rounded-lg text-ink-muted hover:text-ink hover:bg-surface-100">
            <X size={16} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={submit} className="p-5 space-y-4">
          {error && (
            <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>
          )}

          <div>
            <label className="label">Full name *</label>
            <input className="input w-full" value={form.name} onChange={set('name')} placeholder="Jane Smith" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Email</label>
              <input type="email" className="input w-full" value={form.email} onChange={set('email')} placeholder="jane@example.com" />
            </div>
            <div>
              <label className="label">Phone</label>
              <input type="tel" className="input w-full" value={form.phone} onChange={set('phone')} placeholder="+1 555 000 0000" />
            </div>
          </div>

          <div>
            <label className="label">Date of Birth <span className="text-ink-muted font-normal">(optional)</span></label>
            <input
              type="date"
              className="input w-full"
              value={form.date_of_birth}
              onChange={set('date_of_birth')}
              max={new Date().toISOString().split('T')[0]}
            />
          </div>

          <div>
            <label className="label">Notes</label>
            <textarea
              className="input w-full resize-none"
              rows={3}
              value={form.notes}
              onChange={set('notes')}
              placeholder="Any notes about this client…"
            />
          </div>

          <div className="flex gap-3 pt-1">
            <button type="submit" disabled={saving} className="btn btn-primary flex-1">
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            <button type="button" onClick={onClose} className="btn btn-secondary">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function Clients() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [search,  setSearch]  = useState('');
  const [page,    setPage]    = useState(1);
  const [deleting, setDeleting] = useState(null);
  const [editing,  setEditing]  = useState(null);
  const limit = 25;

  const { data, isLoading } = useQuery({
    queryKey: ['clients', { search, page }],
    queryFn: () => fetchClients({ ...(search && { search }), page, limit }),
    keepPreviousData: true,
  });

  const clients = data?.data || [];
  const total   = data?.meta?.total || 0;
  const pages   = Math.ceil(total / limit);

  const deleteMutation = useMutation({
    mutationFn: deleteClient,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients'] });
      setDeleting(null);
    },
  });

  const handleSaved = () => {
    qc.invalidateQueries({ queryKey: ['clients'] });
    setEditing(null);
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-lg font-semibold text-ink">Clients</h1>
          <p className="text-sm text-ink-secondary mt-0.5">{total} total</p>
        </div>
      </div>

      {/* Search */}
      <div className="card p-3 mb-4 flex items-center gap-2">
        <Search size={14} className="text-ink-muted shrink-0" />
        <input
          className="flex-1 text-sm bg-transparent outline-none text-ink placeholder-ink-muted"
          placeholder="Search by name, email or phone…"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
        />
      </div>

      {/* List */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="p-10 text-center text-ink-muted text-sm">Loading…</div>
        ) : !clients.length ? (
          <div className="p-10 text-center text-ink-muted text-sm">No clients found.</div>
        ) : (
          <ul className="divide-y divide-surface-border">
            {clients.map(c => (
              <li key={c.id}>
                <div className="flex items-center gap-3 px-4 py-3 hover:bg-surface-50 transition-colors">
                  <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white text-xs font-semibold shrink-0">
                    {c.name.charAt(0).toUpperCase()}
                  </div>

                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/clients/${c.id}`)}>
                    <p className="text-sm font-medium text-ink">{c.name}</p>
                    <p className="text-xs text-ink-muted truncate">
                      {[c.email, c.phone].filter(Boolean).join(' · ') || 'No contact info'}
                    </p>
                  </div>

                  {/* DOB column */}
                  <div className="hidden sm:block text-xs text-ink-muted shrink-0 w-24 text-right">
                    {(c.date_of_birth || c.dob)
                      ? new Date(c.date_of_birth || c.dob).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                      : <span>—</span>
                    }
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setEditing(c)}
                      className="p-1.5 rounded-lg text-ink-muted hover:text-primary hover:bg-blue-50 transition-colors"
                      title="Edit client"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      onClick={() => setDeleting(c)}
                      className="p-1.5 rounded-lg text-ink-muted hover:text-red-500 hover:bg-red-50 transition-colors"
                      title="Delete client"
                    >
                      <Trash2 size={14} />
                    </button>
                    <button
                      onClick={() => navigate(`/clients/${c.id}`)}
                      className="p-1.5 rounded-lg text-ink-muted hover:text-ink hover:bg-surface-100 transition-colors"
                      title="View details"
                    >
                      <ChevronRight size={14} />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}

        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-surface-border">
            <p className="text-xs text-ink-muted">Page {page} of {pages}</p>
            <div className="flex gap-2">
              <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="btn btn-secondary text-xs py-1.5 px-3">Prev</button>
              <button disabled={page === pages} onClick={() => setPage(p => p + 1)} className="btn btn-secondary text-xs py-1.5 px-3">Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Edit modal */}
      {editing && (
        <ClientEditModal
          client={editing}
          onClose={() => setEditing(null)}
          onSaved={handleSaved}
        />
      )}

      {/* Delete confirm */}
      {deleting && (
        <ConfirmDialog
          title="Delete client"
          message={`Remove "${deleting.name}"? Their booking history will be preserved.`}
          confirmLabel="Delete"
          variant="danger"
          onConfirm={() => deleteMutation.mutate(deleting.id)}
          onCancel={() => setDeleting(null)}
          loading={deleteMutation.isPending}
        />
      )}
    </div>
  );
}
